import {DocumentNode, getNamedType, GraphQLType, isInterfaceType, isObjectType} from 'query-api-graphql';
import {print as astToString} from 'query-api-graphql';

import {DATA_FRAME_DISPLAY_ROW_LIMIT} from 'analysis-tool/AnalysisToolConstants';
import {
  buildPreviewFailureMessage,
  buildPreviewOverRowLimitMessage,
} from 'analysis-tool/data-frame-creation/DataFrameCreationMessages';
import {assert, assertAndContinue} from 'Asserts';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {GraphQLField} from 'query-api/graphql-field-selector/GraphQLFieldSelectorTypes';
import {generateDatasetPreview} from 'query-api/QueryApiDAO';
import {FILTER_KEY_TO_QUERY_API_ARG_NAME} from 'query-api/QueryApiFilters';
import {QueryApiIntrospection, UseQueryApiIntrospectionOutput} from 'query-api/useQueryApiIntrospection';
import {FilterKey} from 'search/SearchConstants';
import {InlineBannerProps} from 'ui-library/banners/InlineBanner';
import {makeAsyncCall, AsyncState, getMostRecentData, AsyncStatus} from 'ui-library/hooks/useAsyncState';
import * as Logging from 'util/Logging';

import {DEFAULT_FIELD, PREVIEW_LIMIT} from './SchemaDatasetCreationConstants';

/** package */
export type PreviewState = AsyncState<{dataFrame: DataFrame}>;

/** package */
export function typeHasDefaultField(type: GraphQLType): boolean {
  const namedType = getNamedType(type);
  return (
    (isInterfaceType(namedType) || isObjectType(namedType)) && namedType.getFields()[DEFAULT_FIELD] != null
  );
}

/** package */
export function fieldHasArgument(field: GraphQLField, argumentName: string): boolean {
  return field.args.some((arg) => arg.name === argumentName);
}

/** package */
export function fieldHasRegistryArg(field: GraphQLField): boolean {
  return fieldHasArgument(field, FILTER_KEY_TO_QUERY_API_ARG_NAME[FilterKey.QUERY_API_REGISTRY]);
}

function fieldHasArchiveReasonArg(field: GraphQLField): boolean {
  return fieldHasArgument(field, FILTER_KEY_TO_QUERY_API_ARG_NAME[FilterKey.QUERY_API_ARCHIVE_REASON]);
}

/** package */
export function assertHasArchiveReasonArg(
  schemaListingField: GraphQLField,
  errorLoggingData: {
    schemaId: string;
    schemaName: string;
    listingFieldName: string;
  }
): boolean {
  const hasArchiveReasonArg = fieldHasArchiveReasonArg(schemaListingField);
  assertAndContinue(
    hasArchiveReasonArg,
    'User selected a schema without an archiveReason argument',
    errorLoggingData
  );
  return hasArchiveReasonArg;
}

/** package */
export function getPreviewFnForDatasetFromQueryApi(
  queryApiIntrospection: UseQueryApiIntrospectionOutput,
  previewState: PreviewState
) {
  return (
    requestData: {queryAst: DocumentNode},
    setAsyncState: (previewState: PreviewState) => void
  ): Promise<void> => {
    assert(
      queryApiIntrospection.status === QueryResultType.SUCCESS,
      'Tried to generate a preview before introspection finished'
    );

    const validationErrors = queryApiIntrospection.getQueryAstValidationErrors(requestData.queryAst);
    if (validationErrors.length > 0) {
      Logging.warn('Tried to preview an invalid query', {
        extra: {
          query: astToString(requestData.queryAst),
          errors: validationErrors.map((error) => error.message),
        },
      });
    }

    return makeAsyncCall({
      args: [requestData],
      asyncState: previewState,
      makeCall: ({queryAst}) => generateDatasetPreview(astToString(queryAst)),
      setAsyncState,
    });
  };
}

/** package */
export function getFieldDisplayNameFn(
  queryListingField: GraphQLField,
  queryApiIntrospection: QueryApiIntrospection
) {
  return (field: GraphQLField): string =>
    // Special-case the top-level field.
    // It's named <org>__<schema_name>ItemsPreview but we really just want to show <schema_name>.
    field === queryListingField
      ? queryApiIntrospection.getTypeDisplayName(getNamedType(queryListingField.type))
      : queryApiIntrospection.getFieldDisplayName(field);
}

/** package */
export function buildErrorAndWarningMessages(previewState: PreviewState): Array<InlineBannerProps> {
  if (previewState.status === AsyncStatus.ERROR) {
    return [buildPreviewFailureMessage()];
  }

  const previewData = getMostRecentData(previewState);
  if (previewData?.dataFrame == null) {
    return [];
  }

  if (previewData.dataFrame.rows.length >= PREVIEW_LIMIT) {
    return [
      buildPreviewOverRowLimitMessage(
        PREVIEW_LIMIT,
        `The final table will be limited to the first ${DATA_FRAME_DISPLAY_ROW_LIMIT.toLocaleString()} rows.`
      ),
    ];
  }

  return [];
}
